// StatsBar — horizontal scrollable stats panel.
import React from "react";
import { tLm } from "../../i18n/liqmap";
import { useLanguage } from "../../i18n/LanguageContext";

const fmtUsd = (n) => {
  // Never show "—" — coerce null/undefined/NaN to 0 so users always see a value.
  const v = (n == null || isNaN(n)) ? 0 : Number(n);
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const fmtPct = (n) => (n == null || isNaN(n) ? "--%" : `${n > 0 ? "+" : ""}${n.toFixed(2)}%`);

const Stat = ({ label, value, color = "white", testid, onClick, tooltip }) => {
  const interactive = typeof onClick === "function";
  const Wrapper = interactive ? "button" : "div";
  return (
    <Wrapper
      type={interactive ? "button" : undefined}
      onClick={interactive ? onClick : undefined}
      className={
        "min-w-[110px] shrink-0 text-left " +
        (interactive ? "cursor-pointer rounded-md outline-none transition-all hover:bg-white/[0.04] focus-visible:ring-1 focus-visible:ring-white/30" : "")
      }
      data-testid={testid}
    >
      <p className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.18em] text-white/40">
        <span>{label}</span>
        {tooltip && (
          <span
            title={tooltip}
            data-testid={`${testid}-tooltip`}
            aria-label={tooltip}
            className="cursor-help text-white/30 transition hover:text-white/60"
          >
            ⓘ
          </span>
        )}
      </p>
      <p className="font-mono text-sm font-black" style={{ color }}>{value}</p>
    </Wrapper>
  );
};

// ─── Real-liq coverage badge ────────────────────────────────────────────────
// status: warming_up | live | partial | degraded | offline | (undefined → hidden)
const CoverageBadge = ({ status, activeCount, warmingMin, t }) => {
  if (!status) return null;
  let label = "";
  let color = "";
  let bg = "";
  if (status === "warming_up") {
    label = (t.liq_real_badge_warming || "WARMING UP · {m}m left").replace("{m}", String(warmingMin ?? 60));
    color = "#a855f7"; bg = "rgba(168,85,247,0.15)";
  } else if (status === "live") {
    label = t.liq_real_badge_live || "LIVE · OKX/BG/HL";
    color = "#00d084"; bg = "rgba(0,208,132,0.15)";
  } else if (status === "partial") {
    label = (t.liq_real_badge_partial || "PARTIAL · {n}/3 sources").replace("{n}", String(activeCount ?? 2));
    color = "#f97316"; bg = "rgba(249,115,22,0.15)";
  } else if (status === "degraded") {
    label = t.liq_real_badge_degraded || "DEGRADED · 1/3 source";
    color = "#ff3b5c"; bg = "rgba(255,59,92,0.15)";
  } else {
    label = t.liq_real_badge_offline || "OFFLINE · no feed";
    color = "#9ca3af"; bg = "rgba(156,163,175,0.15)";
  }
  return (
    <div
      data-testid="liqmap-liq-real-badge"
      data-status={status}
      className="shrink-0 self-center rounded-full px-3 py-1 font-mono text-[10px] font-bold tracking-wider"
      style={{ color, background: bg, border: `1px solid ${color}` }}
    >
      {label}
    </div>
  );
};

export default function StatsBar({
  price, change24, openInterest,
  liqLongs, liqShorts,        // values to render (real or projected fallback)
  liqRealStatus,              // "warming_up" | "live" | "partial" | "degraded" | "offline"
  liqRealSourcesActive,       // ["okx","bitget","hyperliquid"]
  liqRealWarmingSecondsRemaining,
  liqRealCoverageNote,
  clusterMax, divergence, defiTvlAtRisk, defiProtocols, whaleVolume, onClusterMaxClick,
}) {
  const { lang } = useLanguage();
  const t = tLm(lang).stats;
  const chColor = change24 == null ? "white" : change24 >= 0 ? "#00d084" : "#ff3b5c";

  // DEX/CEX divergence badge — orange ≥2%, red ≥5%, hidden < 2%.
  const divPct = divergence?.pct;
  const showDiv = divPct != null && divPct > 2;
  const isAlert = divPct != null && divPct > 5;
  const divTitle = divergence
    ? `CEX cluster: ${fmtUsd(divergence.cexMax)} · DEX cluster: ${fmtUsd(divergence.dexMax)}`
    : "";

  // Decide labels + tooltip based on real-liq coverage status.
  // During warming → keep "EXPOSICIÓN ESTIMADA" wording + projection tooltip.
  // After warming  → "LIQ LONGS 24H" + real-liq tooltip.
  const isReal = liqRealStatus && liqRealStatus !== "warming_up";
  const longsLabel  = isReal ? (t.liq_longs_real  || t.liq_longs)  : t.liq_longs;
  const shortsLabel = isReal ? (t.liq_shorts_real || t.liq_shorts) : t.liq_shorts;
  const longsTooltip  = isReal ? (t.liq_real_tooltip || t.liq_longs_tooltip)  : t.liq_longs_tooltip;
  const shortsTooltip = isReal ? (t.liq_real_tooltip || t.liq_shorts_tooltip) : t.liq_shorts_tooltip;
  const activeCount = (liqRealSourcesActive || []).length;
  const warmingMin = liqRealWarmingSecondsRemaining != null
    ? Math.max(1, Math.ceil(liqRealWarmingSecondsRemaining / 60))
    : null;

  return (
    <div
      className="flex gap-6 overflow-x-auto rounded-2xl border border-[#f97316]/15 bg-[#080508]/70 p-4 backdrop-blur-md scrollbar-thin"
      data-testid="liqmap-stats-bar"
    >
      <Stat label={t.price} value={fmtUsd(price)} color="#f97316" testid="liqmap-stat-price" />
      <Stat label={t.change24} value={fmtPct(change24)} color={chColor} testid="liqmap-stat-24h" />
      <Stat label={t.oi} value={fmtUsd(openInterest)} testid="liqmap-stat-oi" />
      <Stat label={longsLabel}  value={fmtUsd(liqLongs)}  color="#00d084" testid="liqmap-stat-liq-longs"  tooltip={longsTooltip} />
      <Stat label={shortsLabel} value={fmtUsd(liqShorts)} color="#ff3b5c" testid="liqmap-stat-liq-shorts" tooltip={shortsTooltip} />
      <CoverageBadge
        status={liqRealStatus}
        activeCount={activeCount}
        warmingMin={warmingMin}
        t={t}
      />
      <Stat
        label={t.cluster_max}
        value={fmtUsd(clusterMax)}
        color="#a855f7"
        testid="liqmap-stat-cluster"
        onClick={clusterMax != null ? onClusterMaxClick : undefined}
      />
      {/* DeFi TVL at risk (on-chain lending markets) — only when > 0 */}
      {defiTvlAtRisk > 0 && (
        <div
          title={(defiProtocols || []).length ? `Protocolos: ${(defiProtocols || []).join(", ")}` : ""}
          data-testid="liqmap-stat-defi-tvl"
          className="min-w-[110px] shrink-0 text-left"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/40">DEFI EN RIESGO</p>
          <p className="font-mono text-sm font-black" style={{ color: "#a855f7" }}>
            {fmtUsd(defiTvlAtRisk)}
          </p>
        </div>
      )}
      {/* Whale orderbook volume (Layer 5 toggle) — only when > 0 */}
      {whaleVolume > 0 && (
        <div
          title="Volumen total de órdenes ≥ $1M detectadas en orderbooks (CEX + DEX)"
          data-testid="liqmap-stat-whale-vol"
          className="min-w-[110px] shrink-0 text-left"
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/40">VOL BALLENAS</p>
          <p className="font-mono text-sm font-black" style={{ color: "#FFD700" }}>
            🐋 {fmtUsd(whaleVolume)}
          </p>
        </div>
      )}
      {showDiv && (
        <div
          title={divTitle}
          data-testid="liqmap-divergence-badge"
          className={
            "shrink-0 self-center rounded-full px-3 py-1 font-mono text-[10px] font-bold transition-all " +
            (isAlert
              ? "animate-[pulse_1s_ease-in-out_infinite] border border-[#ff3b5c] text-[#ff3b5c]"
              : "animate-[pulse_2.4s_ease-in-out_infinite] border border-[#f97316] text-[#f97316]")
          }
          style={{
            background: isAlert ? "rgba(255,59,92,0.15)" : "rgba(249,115,22,0.15)",
          }}
        >
          {isAlert ? "🚨" : "⚡"} DEX/CEX +{divPct.toFixed(1)}% {isAlert ? "ALERTA" : "divergencia"}
        </div>
      )}
    </div>
  );
}
